/**
 * ReplayController — floating panel for Bar Replay mode (Round 68a).
 *
 * Pure presentational component: parent owns the playback state (active /
 * currentIdx / playing / speed) and the candles array. This component just
 * wires user input to callbacks.
 *
 * Layout: bottom-centre of the chart, video-player aesthetic.
 *   ⏮  ⏪  ▶/⏸  ⏩  ⏭          [scrubber]         1× ▾
 *   2026-05-17 03:15 IST  ·  bar 1240/2000  ·  760 bars remaining
 */
import React, { useState, useRef, useEffect } from 'react';
import { tokens } from '../../styles/colorTokens';
import {
  Box, IconButton, MenuItem, Popover, Select, Slider, TextField, Tooltip, Typography, CircularProgress,
} from '@mui/material';
import SkipPreviousIcon from '@mui/icons-material/SkipPrevious';
import FastRewindIcon from '@mui/icons-material/FastRewind';
import FastForwardIcon from '@mui/icons-material/FastForward';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import CloseIcon from '@mui/icons-material/Close';
import EventIcon from '@mui/icons-material/Event';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

export interface ReplayControllerProps {
  /** Total candles in the underlying dataset — drives the scrubber's
   *  maximum value. Should equal paneData[0].length. */
  totalBars: number;
  /** Current playhead index into the dataset (0-based). */
  currentIdx: number;
  playing: boolean;
  /** Bars revealed per second. 1 = one bar every second; 8 = 8 bars/s. */
  speed: number;
  /** Display string for the current bar's timestamp. Formatted by parent
   *  so this component stays date-library-free. */
  currentTimeLabel: string;
  isDark: boolean;
  onPlayPause: () => void;
  onStepBack: () => void;
  onStepForward: () => void;
  onRestart: () => void;
  onJumpToEnd: () => void;
  onSpeedChange: (s: number) => void;
  /** Scrub to absolute index. */
  onSeek: (idx: number) => void;
  /** Close the panel and exit replay mode entirely. */
  onClose: () => void;
  /** R68b — pick start by exact IST date/time. Parent matches the picked
   *  moment to the closest candle and calls onSeek. Display string
   *  "YYYY-MM-DDTHH:mm" is what <input type="datetime-local"> expects, so
   *  this callback hands one back unchanged. */
  onPickDateTime?: (isoLocal: string) => void;
  /** Pre-fill value for the date picker — usually the current playhead's
   *  formatted timestamp. */
  currentIsoLocal?: string;
  /** R142 — replay data feed selector. */
  source?: 'broker' | 'free';
  /** A deep window has been loaded from the selected source. */
  deepLoaded?: boolean;
  /** Deep-history fetch in flight. */
  deepLoading?: boolean;
  /** User picked a replay data source — parent loads the deep window. */
  onSourceChange?: (s: 'broker' | 'free') => void;
  /** Step-forward/back increment (label). Parent maps it to a bar count using
   *  the chart interval. */
  stepLabel?: string;
  onStepLabelChange?: (s: string) => void;
  /** Step sizes worth offering at the current chart interval. The parent filters
   *  out anything smaller than one bar. Defaults to the full list. */
  stepOptions?: string[];
}

const SPEED_OPTIONS = [1, 2, 3, 4, 5, 7, 8];
export const REPLAY_STEP_OPTIONS = ['1 Bar', '1 Hour', '4 Hour', '1 Day', '1 Week', '1 Month'];

const ReplayController: React.FC<ReplayControllerProps> = ({
  totalBars, currentIdx, playing, speed, currentTimeLabel, isDark,
  onPlayPause, onStepBack, onStepForward, onRestart, onJumpToEnd,
  onSpeedChange, onSeek, onClose, onPickDateTime, currentIsoLocal,
  source = 'broker', deepLoaded, deepLoading, onSourceChange,
  stepLabel = '1 Bar', onStepLabelChange, stepOptions,
}) => {
  // This component is handed `isDark` rather than using the theme hook, so the tokens are
  // derived from it. Same palette, same onSolid rule as everywhere else.
  const C = tokens[isDark ? 'dark' : 'light'];
  const atStart = currentIdx <= 0;
  const atEnd = currentIdx >= totalBars - 1;
  const remaining = Math.max(0, totalBars - 1 - currentIdx);
  const [pickerAnchor, setPickerAnchor] = useState<HTMLElement | null>(null);
  const [pickerValue, setPickerValue] = useState<string>(currentIsoLocal || '');
  // Re-sync the picker default whenever the parent's current timestamp moves
  // so reopening shows the playhead's current time, not a stale earlier one.
  React.useEffect(() => { if (currentIsoLocal) setPickerValue(currentIsoLocal); }, [currentIsoLocal]);

  // R142 — draggable panel. `pos` is an (x,y) offset from the default
  // bottom-centre anchor, persisted so it survives re-renders / reloads.
  const LS_KEY = 'replay_panel_pos';
  const [pos, setPos] = useState<{ x: number; y: number }>(() => {
    try { const v = JSON.parse(localStorage.getItem(LS_KEY) || ''); if (v && typeof v.x === 'number') return v; } catch { /* ignore */ }
    return { x: 0, y: 0 };
  });
  useEffect(() => { try { localStorage.setItem(LS_KEY, JSON.stringify(pos)); } catch { /* ignore */ } }, [pos]);
  const dragRef = useRef<{ sx: number; sy: number; ox: number; oy: number } | null>(null);
  const onDragStart = (e: React.PointerEvent) => {
    e.preventDefault();
    try { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId); } catch { /* ignore */ }
    dragRef.current = { sx: e.clientX, sy: e.clientY, ox: pos.x, oy: pos.y };
  };
  const onDragMove = (e: React.PointerEvent) => {
    const d = dragRef.current; if (!d) return;
    // Clamp loosely to the viewport so the panel can't be lost off-screen.
    const maxX = Math.max(100, window.innerWidth / 2 - 40);
    const maxY = Math.max(100, window.innerHeight - 120);
    const nx = Math.max(-maxX, Math.min(maxX, d.ox + (e.clientX - d.sx)));
    const ny = Math.max(-maxY, Math.min(40, d.oy + (e.clientY - d.sy)));
    setPos({ x: nx, y: ny });
  };
  const onDragEnd = () => { dragRef.current = null; };
  const resetPos = () => setPos({ x: 0, y: 0 });

  return (
    <Box
      data-replay-panel="true"
      sx={{
        position: 'absolute',
        left: '50%', bottom: 36,
        transform: `translate(calc(-50% + ${pos.x}px), ${pos.y}px)`,
        zIndex: 10,
        bgcolor: isDark ? 'rgba(13,17,23,0.95)' : 'rgba(255,255,255,0.97)',
        border: `1px solid ${isDark ? '#1e2d3d' : '#cfd8dc'}`,
        borderRadius: 1.2,
        boxShadow: '0 4px 16px rgba(0,0,0,0.35)',
        px: 1, py: 0.6,
        display: 'flex', flexDirection: 'column', gap: 0.3,
        minWidth: 480, maxWidth: 680,
        pointerEvents: 'auto',
      }}
    >
      {/* Drag handle — grab to move the panel; double-click to recenter. */}
      <Tooltip title="Drag to move · double-click to recenter">
        <Box
          onPointerDown={onDragStart} onPointerMove={onDragMove} onPointerUp={onDragEnd}
          onPointerCancel={onDragEnd} onDoubleClick={resetPos}
          sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            height: 14, mb: 0.1, cursor: 'grab', touchAction: 'none', opacity: 0.45,
            '&:hover': { opacity: 0.8 }, '&:active': { cursor: 'grabbing' },
          }}
        >
          <DragIndicatorIcon sx={{ fontSize: 15, transform: 'rotate(90deg)' }} />
        </Box>
      </Tooltip>

      {/* Top row — transport controls + scrubber + speed */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
        <Tooltip title="Restart (jump to start)">
          <span><IconButton size="small" onClick={onRestart} disabled={atStart} sx={{ p: 0.4 }}>
            <SkipPreviousIcon fontSize="small" />
          </IconButton></span>
        </Tooltip>
        <Tooltip title={`Step back ${stepLabel}`}>
          <span><IconButton size="small" onClick={onStepBack} disabled={atStart} sx={{ p: 0.4 }}>
            <FastRewindIcon fontSize="small" />
          </IconButton></span>
        </Tooltip>
        <Tooltip title={playing ? 'Pause' : 'Play'}>
          <IconButton size="small" onClick={onPlayPause} sx={{
            p: 0.4, bgcolor: '#1e88e5', color: '#fff',
            '&:hover': { bgcolor: '#1565c0' },
          }}>
            {playing ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
        <Tooltip title={`Step forward ${stepLabel}`}>
          <span><IconButton size="small" onClick={onStepForward} disabled={atEnd} sx={{ p: 0.4 }}>
            <FastForwardIcon fontSize="small" />
          </IconButton></span>
        </Tooltip>
        {/* Step increment per click */}
        <Tooltip title="How much each step-forward / step-back moves">
          <Select size="small" value={stepLabel} onChange={(e) => onStepLabelChange?.(e.target.value)}
            sx={{ height: 26, fontSize: '0.66rem', ml: 0.3, '& .MuiSelect-select': { py: 0.2, px: 0.6 } }}>
            {(stepOptions?.length ? stepOptions : REPLAY_STEP_OPTIONS).map(o => <MenuItem key={o} value={o} sx={{ fontSize: '0.7rem' }}>{o}</MenuItem>)}
          </Select>
        </Tooltip>
        <Tooltip title="Jump to end (exit replay)">
          <span><IconButton size="small" onClick={onJumpToEnd} disabled={atEnd} sx={{ p: 0.4 }}>
            <SkipNextIcon fontSize="small" />
          </IconButton></span>
        </Tooltip>

        <Box sx={{ flex: 1, mx: 1.2 }}>
          <Slider
            size="small"
            value={currentIdx}
            min={0}
            max={Math.max(0, totalBars - 1)}
            onChange={(_, v) => onSeek(Array.isArray(v) ? v[0] : v)}
            sx={{ py: 0.5,
              '& .MuiSlider-thumb': { width: 12, height: 12 },
              '& .MuiSlider-track': { height: 3 },
              '& .MuiSlider-rail': { height: 3, opacity: 0.4 },
            }}
          />
        </Box>

        <Select
          size="small"
          value={speed}
          onChange={(e) => onSpeedChange(Number(e.target.value))}
          sx={{ fontSize: '0.74rem', minWidth: 64, height: 26, '& .MuiSelect-select': { py: 0.3 } }}
        >
          {SPEED_OPTIONS.map(s => (
            <MenuItem key={s} value={s} dense sx={{ fontSize: '0.74rem' }}>{s}×</MenuItem>
          ))}
        </Select>

        {onPickDateTime && (
          <Tooltip title="Pick a start time">
            <IconButton size="small" onClick={(e) => setPickerAnchor(e.currentTarget)} sx={{ p: 0.4, ml: 0.4 }}>
              <EventIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Close replay">
          <IconButton size="small" onClick={onClose} sx={{ p: 0.4, ml: 0.4 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Date / time picker popover — click outside or pick to close. */}
      <Popover
        open={!!pickerAnchor}
        anchorEl={pickerAnchor}
        onClose={() => setPickerAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Box sx={{ p: 1.2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: '0.74rem', color: 'text.secondary' }}>Start at:</Typography>
          <TextField
            type="datetime-local"
            size="small"
            value={pickerValue}
            onChange={(e) => setPickerValue(e.target.value)}
            inputProps={{ style: { fontSize: '0.78rem', padding: '4px 6px' } }}
          />
          <Box
            component="button"
            onClick={() => {
              if (pickerValue && onPickDateTime) onPickDateTime(pickerValue);
              setPickerAnchor(null);
            }}
            sx={{
              bgcolor: '#1e88e5', color: '#fff', border: 'none', borderRadius: 0.6,
              fontSize: '0.74rem', fontWeight: 700, px: 1, py: 0.4, cursor: 'pointer',
              '&:hover': { bgcolor: '#1565c0' },
            }}
          >Go</Box>
        </Box>
      </Popover>

      {/* Bottom row — time/bar readout */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1.2,
        fontFamily: 'monospace', fontSize: '0.7rem',
        color: 'text.secondary', px: 0.5,
      }}>
        <Box sx={{ fontWeight: 700, color: 'text.primary' }}>{currentTimeLabel}</Box>
        <Box>·</Box>
        <Box>bar {currentIdx + 1}/{totalBars}</Box>
        <Box>·</Box>
        <Box>{remaining} bars remaining</Box>
        {playing && (
          <Box sx={{ color: '#1e88e5', fontWeight: 700 }}>● PLAYING</Box>
        )}
        {/* R142 — replay data source: Broker (live feed, paginated) vs Free
            (Binance/Yahoo deep history). Replay-only; never affects live charts. */}
        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title="Replay data feed. Broker = your exchange (Delta/Zerodha), paginated for more history. Free = deep history from Binance (crypto) / Yahoo (NSE) — for analysis only, not the data you trade.">
            <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>Data:</Typography>
          </Tooltip>
          {(['broker', 'free'] as const).map(s => {
            const on = !!deepLoaded && source === s;
            return (
              <Box key={s} component="button" onClick={() => onSourceChange?.(s)} disabled={deepLoading}
                sx={{
                  cursor: 'pointer', borderRadius: 0.6, fontSize: '0.66rem', fontWeight: 700,
                  px: 0.7, py: 0.2, fontFamily: 'inherit',
                  border: `1px solid ${on ? '#1e88e5' : (isDark ? '#33414f' : '#cfd8dc')}`,
                  bgcolor: on ? C.neutral : 'transparent', color: on ? C.onSolid : 'text.secondary',
                  '&:disabled': { opacity: 0.5, cursor: 'default' },
                }}>
                {s === 'broker' ? 'Broker' : 'Free'}
              </Box>
            );
          })}
          {deepLoading && <CircularProgress size={12} />}
        </Box>
      </Box>
    </Box>
  );
};

export default ReplayController;
